import React, { useEffect, useMemo } from 'react';
import {
  OrderableItemListItem,
  Props as OrderableItemListItemProps,
} from '@/components/orders/orderable-item-list-item';
import { OrderAgentWorkflowMode } from './types';
import { useBaseItemQueryVariables } from '@/hooks/use-item-query-variables';
import { useDebouncedCallback } from 'use-debounce';
import { useOrderInbounds } from '@/hooks/use-order-inbounds';
import { useToasts } from '@/contexts/toast-context';
import { useUpdateInboundQuantity } from './queries';
import { VariantPackWithQuantities } from '@/hooks/use-order-inbound-reducer';

const DEBOUNCE_DELAY_MS = 1500;

type Props = OrderableItemListItemProps & {
  action?: React.ReactNode;
  restaurantUUID: string;
  smartOrderInputUUID: string | null;
  variantPackWithQuantities: VariantPackWithQuantities;
  workflowMode: OrderAgentWorkflowMode;
};

export const InboundOrderableItemListItem: React.FC<Props> = ({
  action,
  restaurantUUID,
  smartOrderInputUUID,
  updateQuantity,
  variantPackWithQuantities,
  workflowMode,
  ...restProps
}) => {
  const { showErrorToast } = useToasts();
  const baseItemQueryVariables = useBaseItemQueryVariables();
  const [updateQuantityMutation] = useUpdateInboundQuantity();
  const { isViewOnly } = useOrderInbounds();

  const quantityState = useMemo(() => {
    const quantityMap = {
      [variantPackWithQuantities.variantPackUUID]:
        variantPackWithQuantities.quantity,
    };

    return {
      eventPayloadMap: {},
      localQuantityMap: quantityMap,
      savedQuantityMap: quantityMap,
      variantPackDetailMap: {},
    };
  }, [variantPackWithQuantities]);

  // take inputUUID as a parameter to ensure flush is called with the correct inputUUID
  const debouncedUpdateInboundQuantity = useDebouncedCallback(
    async (quantity: number, inputUUID: string) => {
      try {
        await updateQuantityMutation({
          variables: {
            ...baseItemQueryVariables,
            quantity,
            restaurantUUID,
            skipGroupMemberships: true,
            smartOrderInputUUID: inputUUID,
          },
        });
      } catch {
        showErrorToast();
      }
    },
    DEBOUNCE_DELAY_MS,
  );

  useEffect(() => {
    // only run on dismount
    return () => {
      if (smartOrderInputUUID) {
        debouncedUpdateInboundQuantity.flush();
      }
    };
  }, [smartOrderInputUUID, debouncedUpdateInboundQuantity]);

  return (
    <OrderableItemListItem
      action={action}
      disabled={
        isViewOnly || workflowMode === OrderAgentWorkflowMode.Correction
      }
      isInboundOrder
      quantityState={quantityState}
      showVariantPackOrderInfo
      updateQuantity={(updateQuantityInput) => {
        updateQuantity?.(updateQuantityInput);
        const nextQuantity = updateQuantityInput.newQuantity;
        // deletions handled outside of this component
        if (smartOrderInputUUID && nextQuantity !== 0) {
          debouncedUpdateInboundQuantity(
            updateQuantityInput.newQuantity,
            smartOrderInputUUID,
          );
        }
      }}
      {...restProps}
    />
  );
};
